import { formatValidQueryParamUrlCommaSeparated } from "utils/common";
import { formatPatientDocument } from "utils/patient-documents.util";

import { emptySplitApi } from "./empty-split-api";
import {
  PatientDocument,
  PatientDocumentFilter,
  PatientDocumentInput,
  PatientDocumentResponse,
  PatientDocumentSignatureInput
} from "./types/patient-documents.type";
import {
  EDocumentsConnectResponse,
  EDocumentsMoveInput,
  EDocumentsMoveResponse,
  EDocumentsSourceType
} from "./types/patient-e-documents.type";
import { BoxAccessTokenResponse } from "./types/practices.type";

export const DEFAULT_PATIENT_DOCUMENTS_QUERY =
  "{id,title,signed_at,signature,content,created_by{id,first_name,last_name,title,suffix},updated_by{id,first_name,last_name,title,suffix},created_at,updated_at,is_header_included}";

export const DEFAULT_PATIENT_DOCUMENTS_QUERY_WITH_PATIENT_HEADER =
  "{id,title,signed_at,signature,content,created_by{id,first_name,last_name,title,suffix},updated_by{id,first_name,last_name,title,suffix},created_at,updated_at,is_header_included,is_patient_header_included}";

export const DEFAULT_PATIENT_DOCUMENT_QUERY =
  "{id,title,signed_at,signature,content,created_by{id,first_name,last_name,title,suffix},updated_by{id,first_name,last_name,title,suffix},created_at,updated_at,order{id},sale{id},is_header_included}";

export const DEFAULT_PATIENT_DOCUMENT_QUERY_WITH_PATIENT_HEADER =
  "{id,title,signed_at,signature,content,created_by{id,first_name,last_name,title,suffix},updated_by{id,first_name,last_name,title,suffix},created_at,updated_at,order{id},sale{id},is_header_included,is_patient_header_included}";

const patientDocumentsApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getPatientDocument: builder.query<
      PatientDocument,
      { patient: number; id: number; query?: string }
    >({
      query: ({
        patient,
        id,
        query
      }: {
        patient: number;
        id: number;
        query: string;
      }) => {
        return `/patients/${patient}/documents/${id}/?query=${
          query || DEFAULT_PATIENT_DOCUMENT_QUERY
        }`;
      },
      providesTags: ["PatientDocuments"],
      transformResponse: (response: PatientDocument) =>
        formatPatientDocument(response)
    }),
    getPatientDocuments: builder.query<
      PatientDocumentResponse,
      PatientDocumentFilter
    >({
      query: ({ patient, query, ...rest }) => {
        const filtersString = formatValidQueryParamUrlCommaSeparated({
          query: query || DEFAULT_PATIENT_DOCUMENTS_QUERY,
          ordering: "-created_at",
          ...rest
        });

        return `/patients/${patient}/documents/?${filtersString}`;
      },
      providesTags: ["PatientDocuments"],
      transformResponse: (response: PatientDocumentResponse) =>
        response.map(formatPatientDocument)
    }),
    createPatientDocument: builder.mutation<
      PatientDocument,
      PatientDocumentInput
    >({
      query: (input) => ({
        url: `/patients/${input.patient}/documents/`,
        method: "POST",
        body: input
      }),
      invalidatesTags: ["PatientDocuments"]
    }),
    updatePatientDocument: builder.mutation<
      PatientDocument,
      PatientDocumentInput
    >({
      query: ({ id, patient, ...rest }) => ({
        url: `/patients/${patient}/documents/${id}/`,
        method: "PATCH",
        body: rest
      }),
      invalidatesTags: ["PatientDocuments"]
    }),
    updatePatientDocumentSignature: builder.mutation<
      PatientDocument,
      PatientDocumentSignatureInput
    >({
      query: ({ id, patient, formData }) => ({
        url: `/patients/${patient}/documents/${id}/`,
        method: "PATCH",
        body: formData
      }),
      invalidatesTags: ["PatientDocuments"]
    }),
    deletePatientDocument: builder.mutation<
      PatientDocument,
      { patient: number; id: number }
    >({
      query: ({ patient, id }: { patient: number; id: number }) => ({
        url: `/patients/${patient}/documents/${id}/`,
        method: "DELETE"
      }),
      invalidatesTags: ["PatientDocuments"]
    }),
    uploadDocument: builder.mutation<
      PatientDocument,
      {
        id: number;
        type: EDocumentsSourceType;
        formData: FormData;
      }
    >({
      query: ({ id, type, formData }) => {
        return {
          url: `/${type}/${id}/e-documents/upload/`,
          method: "POST",
          body: formData
        };
      },
      invalidatesTags: ["EDocuments"]
    }),
    getBoxAccessToken: builder.query<BoxAccessTokenResponse, void>({
      query: () => "/practices/my/box-access-token/",
      providesTags: ["EDocuments"]
    }),
    updateDocumentsLocation: builder.mutation<
      EDocumentsMoveResponse,
      EDocumentsMoveInput
    >({
      query: ({ id, type, ...body }) => ({
        url: `/${type}/${id}/e-documents/move/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: ["EDocuments"]
    }),
    connectToEDocuments: builder.mutation<EDocumentsConnectResponse, number>({
      query: (patient: number) => ({
        url: `/patients/${patient}/e-documents/connect/`,
        method: "POST"
      }),
      invalidatesTags: ["EDocuments", "PatientDocuments"]
    })
  })
});

export const {
  useGetPatientDocumentQuery,
  useGetPatientDocumentsQuery,
  useCreatePatientDocumentMutation,
  useUpdatePatientDocumentMutation,
  useUpdatePatientDocumentSignatureMutation,
  useDeletePatientDocumentMutation,
  useUploadDocumentMutation,
  useUpdateDocumentsLocationMutation,
  useGetBoxAccessTokenQuery,
  useLazyGetBoxAccessTokenQuery,
  useConnectToEDocumentsMutation
} = patientDocumentsApi;
